import { create } from 'zustand';
import { ITimezoneOption } from 'react-timezone-select';

import { AlertsAndFollowUpQuestion } from '@/types/AnalysisResult';
import { Client } from '@/types/Client';
import {
  Candidate,
  CandidateWithJobContext,
  CandidateWithJobContextBasicInfo
} from '@/types/Candidate';
import { ScreeningType } from '@/types/enums';
import { Job, JobBasicInfo } from '@/types/Job';
import { Screening } from '@/types/Screening';
import { getCandidateFullName, getTimeFromTimestamp } from '@/utils';

const DEFAULT_STEP = 0;
const DEFAULT_RETAKE_LIMIT = 5;
const DEFAULT_SESSION_TIME_LIMIT = 40;

interface CreateInterviewAgent {
  step: number;
  screeningType: ScreeningType;
  selectedTab: 'hierarchical' | 'direct';

  clients: Client[] | null;
  jobs: JobBasicInfo[] | null;
  candidates: CandidateWithJobContext[] | null;

  deadline: Date | null;
  deadlineTime: Date | null;
  timezone: { value: ITimezoneOption } | null;
  sessionName: string;
  client: Client | null;
  job: Job | JobBasicInfo | null;
  candidate: CandidateWithJobContext | CandidateWithJobContextBasicInfo | Candidate | null;
  followUpQuestions: AlertsAndFollowUpQuestion[];
  interviewTitle: string;
  interviewDescription: string;
  retakeLimit: number;
  sessionTimeLimit: number;
  questions: string[];

  // Fixed candidate for resume screening
  fixedCandidate: boolean;

  // Fixed prefill for job interview (from candidate page)
  fixedJobInterview: boolean;
  hasFixedJobInterviewQuestionsInitialized: boolean;

  // Update mode fields
  isUpdateMode: boolean;
  screeningId: string | null;

  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setScreeningType: (type: ScreeningType) => void;
  setSelectedTab: (tab: 'hierarchical' | 'direct') => void;
  setClients: (clients: Client[]) => void;
  setJobs: (jobs: JobBasicInfo[]) => void;
  setCandidates: (candidates: CandidateWithJobContext[]) => void;
  setDeadline: (date: Date) => void;
  setDeadlineTime: (time: Date) => void;
  setTimezone: (timezone: { value: ITimezoneOption }) => void;
  setSessionName: (title: string) => void;
  setClient: (client: Client | null) => void;
  setJob: (job: Job | null) => void;
  setCandidate: (candidate: CandidateWithJobContext | Candidate | null) => void;
  setFollowUpQuestions: (questions: AlertsAndFollowUpQuestion[]) => void;
  setInterviewTitle: (title: string) => void;
  setInterviewDescription: (description: string) => void;
  setRetakeLimit: (limit: number) => void;
  setSessionTimeLimit: (limit: number) => void;
  setQuestions: (questions: string[]) => void;

  // Fixed candidate for resume screening
  initializeResumeScreening: (candidate: Candidate) => void;

  // Fixed prefill for job interview (from candidate page)
  initializeJobInterview: (
    client: Client,
    job: Job | JobBasicInfo,
    candidate: CandidateWithJobContext
  ) => void;
  setFixedJobInterviewQuestionsInitialized: (initialized: boolean) => void;

  // Update mode methods
  setUpdateMode: (isUpdateMode: boolean) => void;
  setScreeningId: (screeningId: string | null) => void;
  initializeFromScreening: (screening: Screening, timezone: ITimezoneOption) => void;

  reset: () => void;
}

export const useCreateInterviewAgent = create<CreateInterviewAgent>((set) => ({
  step: DEFAULT_STEP,
  screeningType: ScreeningType.JobInterview,
  selectedTab: 'hierarchical',
  clients: null,
  jobs: null,
  candidates: null,
  deadline: null,
  deadlineTime: null,
  timezone: null,
  sessionName: '',
  client: null,
  job: null,
  candidate: null,
  followUpQuestions: [],
  interviewTitle: '',
  interviewDescription: '',
  retakeLimit: DEFAULT_RETAKE_LIMIT,
  sessionTimeLimit: DEFAULT_SESSION_TIME_LIMIT,
  questions: [],

  // Fixed candidate for resume screening
  fixedCandidate: false,

  // Fixed prefill for job interview (from candidate page)
  fixedJobInterview: false,
  hasFixedJobInterviewQuestionsInitialized: false,

  // Update mode fields
  isUpdateMode: false,
  screeningId: null,

  setStep: (step: number) => set({ step }),
  nextStep: () => set((state) => ({ step: state.step + 1 })),
  prevStep: () => set((state) => ({ step: state.step - 1 })),
  setScreeningType: (screeningType: ScreeningType) => set({ screeningType }),
  setSelectedTab: (selectedTab: 'hierarchical' | 'direct') => set({ selectedTab }),
  setClients: (clients: Client[]) => set({ clients }),
  setJobs: (jobs: JobBasicInfo[]) => set({ jobs }),
  setCandidates: (candidates: CandidateWithJobContext[]) => set({ candidates }),
  setDeadline: (date: Date) => set({ deadline: date }),
  setDeadlineTime: (time: Date) => set({ deadlineTime: time }),
  setTimezone: (timezone: { value: ITimezoneOption }) => set({ timezone }),
  setSessionName: (sessionName: string) => set({ sessionName }),
  setClient: (client: Client | null) => set({ client }),
  setJob: (job: Job | null) => set({ job }),
  setCandidate: (candidate: CandidateWithJobContext | Candidate | null) =>
    set({ candidate: candidate }),
  setFollowUpQuestions: (questions: AlertsAndFollowUpQuestion[]) =>
    set({ followUpQuestions: questions }),
  setInterviewTitle: (title: string) => set({ interviewTitle: title }),
  setInterviewDescription: (description: string) => set({ interviewDescription: description }),
  setRetakeLimit: (limit: number) => set({ retakeLimit: limit }),
  setSessionTimeLimit: (limit: number) => set({ sessionTimeLimit: limit }),
  setQuestions: (questions: string[]) => set({ questions }),

  // Fixed candidate for resume screening
  initializeResumeScreening: (candidate: Candidate) => {
    set((state) => ({
      ...state,
      isUpdateMode: false,
      fixedCandidate: true,
      screeningType: ScreeningType.ResumeScreening,
      selectedTab: 'direct',
      candidate,
      sessionName: `Interview with ${getCandidateFullName(
        candidate.first_name,
        candidate.last_name
      )}`,
      interviewTitle: '',
      interviewDescription: '',
      retakeLimit: DEFAULT_RETAKE_LIMIT,
      sessionTimeLimit: DEFAULT_SESSION_TIME_LIMIT,
      questions: []
    }));
  },

  // Fixed prefill for job interview (from candidate page)
  initializeJobInterview: (
    client: Client,
    job: Job | JobBasicInfo,
    candidate: CandidateWithJobContext
  ) => {
    set((state) => ({
      ...state,
      isUpdateMode: false,
      fixedJobInterview: true,
      fixedCandidate: false,
      hasFixedJobInterviewQuestionsInitialized: false,
      screeningType: ScreeningType.JobInterview,
      selectedTab: 'hierarchical',
      client,
      job,
      candidate,
      sessionName: `Interview with ${getCandidateFullName(
        candidate.first_name,
        candidate.last_name
      )}`,
      interviewTitle: '',
      interviewDescription: '',
      retakeLimit: DEFAULT_RETAKE_LIMIT,
      sessionTimeLimit: DEFAULT_SESSION_TIME_LIMIT,
      questions: []
    }));
  },

  setFixedJobInterviewQuestionsInitialized: (initialized: boolean) =>
    set({ hasFixedJobInterviewQuestionsInitialized: initialized }),

  // Update mode methods
  setUpdateMode: (isUpdateMode: boolean) => set({ isUpdateMode }),
  setScreeningId: (screeningId: string | null) => set({ screeningId }),
  initializeFromScreening: (screening: Screening, timezone: ITimezoneOption) => {
    const deadlineDate = getTimeFromTimestamp(screening.deadline, screening.deadline_timezone);

    set({
      isUpdateMode: true,
      screeningId: screening.screening_id,
      screeningType: screening.screening_type,
      selectedTab:
        screening.screening_type === ScreeningType.JobInterview ? 'hierarchical' : 'direct',
      sessionName: screening.session_name,
      deadline: deadlineDate,
      deadlineTime: deadlineDate,
      interviewTitle: screening.interview_title,
      interviewDescription: screening.interview_description,
      retakeLimit: screening.retake_limit,
      sessionTimeLimit: screening.session_time_limit,
      questions: screening.questions.map((q) => q.question),
      timezone: {
        value: timezone
      },
      candidate: screening.candidate ?? null,
      job: screening.job ?? null,
      client: screening.job?.client ?? null
    });
  },

  reset: () =>
    set({
      step: DEFAULT_STEP,
      selectedTab: 'hierarchical',
      deadline: null,
      deadlineTime: null,
      timezone: null,
      sessionName: '',
      client: null,
      job: null,
      candidate: null,
      interviewTitle: '',
      interviewDescription: '',
      retakeLimit: DEFAULT_RETAKE_LIMIT,
      sessionTimeLimit: DEFAULT_SESSION_TIME_LIMIT,
      questions: [],
      isUpdateMode: false,
      fixedCandidate: false,
      fixedJobInterview: false,
      hasFixedJobInterviewQuestionsInitialized: false,
      screeningId: null,
      followUpQuestions: []
    })
}));
